import { IconButton, Menu, Portal } from "@chakra-ui/react-v3";
import { DownloadIcon } from "@chakra-ui/icons";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useState, useMemo } from "react";
import { TableRow } from "../../utils/tables-metadata";
import { DocumentReferenceRowData } from "./raw-documents-rows";
import { useDownloadFile } from "../../../shared-logic/useDownloadFile";
import { BaseTableLayout, ColumnDef } from "../../components/base-table-layout";

export type RawDocumentsTableProps = {
  rows: TableRow[];
  onRowClick: (content: React.ReactNode) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onDocumentDownloaded?: (docId: string) => void;
  isRowViewed?: (row: TableRow) => boolean;
};

function isXml(mimeType: string | undefined): boolean {
  return mimeType ? mimeType.includes("xml") : false;
}

export function RawDocumentsTable({
  rows,
  filterQuery,
  onDocumentDownloaded,
  isRowViewed,
}: RawDocumentsTableProps) {
  const { onDownloadFile, isDownloading } = useDownloadFile();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const columns = useMemo<ColumnDef<DocumentReferenceRowData>[]>(() => {
    return [
      { key: "description", header: "Description" },
      { key: "size", header: "Size" },
      { key: "fileType", header: "File Type" },
      { key: "organization", header: "Organization" },
      { key: "date", header: "Date", sort: "desc", maxWidth: "150px" },
      {
        key: "actions" as keyof DocumentReferenceRowData,
        header: "Actions",
        maxWidth: "80px",
        render: (row: DocumentReferenceRowData) => {
          const isMenuOpen = openMenuId === row.id;
          return (
            <Menu.Root
              open={isMenuOpen}
              onOpenChange={(details: { open: boolean }) => {
                setOpenMenuId(details.open ? row.id : null);
              }}
            >
              <Menu.Trigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Download options"
                  loading={isDownloading[row.id]?.downloading}
                >
                  <DownloadIcon />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {isXml(row.fileType) ? (
                      <>
                        <Menu.Item
                          value="pdf"
                          onClick={() => {
                            onDownloadFile({
                              docId: row.id,
                              docFilename: row.fileName,
                              docExtension: row.fileType,
                              conversionType: "pdf",
                            });
                            onDocumentDownloaded?.(row.id);
                          }}
                          disabled={
                            isDownloading[row.id]?.downloading &&
                            isDownloading[row.id]?.type === "pdf"
                          }
                        >
                          <DownloadIcon mr={2} />
                          PDF
                        </Menu.Item>
                        <Menu.Item
                          value="html"
                          onClick={() => {
                            onDownloadFile({
                              docId: row.id,
                              docFilename: row.fileName,
                              docExtension: row.fileType,
                              conversionType: "html",
                            });
                            onDocumentDownloaded?.(row.id);
                          }}
                          disabled={
                            isDownloading[row.id]?.downloading &&
                            isDownloading[row.id]?.type === "html"
                          }
                        >
                          <DownloadIcon mr={2} />
                          HTML
                        </Menu.Item>
                        <Menu.Item
                          value="xml"
                          onClick={() => {
                            onDocumentDownloaded?.(row.id);
                            onDownloadFile({
                              docId: row.id,
                              docFilename: row.fileName,
                              docExtension: row.fileType,
                            });
                          }}
                          disabled={
                            isDownloading[row.id]?.downloading &&
                            isDownloading[row.id]?.type === "xml"
                          }
                        >
                          <DownloadIcon mr={2} />
                          XML
                        </Menu.Item>
                      </>
                    ) : (
                      <Menu.Item
                        value="download"
                        onClick={() => {
                          onDocumentDownloaded?.(row.id);
                          onDownloadFile({
                            docId: row.id,
                            docFilename: row.fileName,
                            docExtension: row.fileType,
                          });
                        }}
                        disabled={isDownloading[row.id]?.downloading}
                      >
                        <DownloadIcon mr={2} />
                        Download
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          );
        },
      },
    ];
  }, [isDownloading, onDownloadFile, openMenuId, onDocumentDownloaded]);

  if (rows.length === 0) return null;

  return (
    <BaseTableLayout
      title="Raw Documents"
      rows={rows as unknown as DocumentReferenceRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      isRowViewed={isRowViewed}
      onRowClick={function () {
        return;
      }}
      getRowStyle={() => ({
        cursor: "inherit",
        _hover: { bg: "bg.subtle" },
      })}
    />
  );
}
